import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

type OpenXAgentFeedbackMutationVariables = {
  langfuse_trace_id: string;
  score: boolean;
  comment?: string | null;
};

type OpenXAgentFeedbackMutationData = {
  openXAgentFeedback?: {
    message: string;
  } | null;
};

type OpenXAgentRegisterFeedbackMutationFn = (options: {
  variables: OpenXAgentFeedbackMutationVariables;
}) => Promise<{ data?: OpenXAgentFeedbackMutationData }>;

export type OpenXAgentFeedbackPayload = {
  langfuseTraceId: string;
  score: boolean;
  comment?: string;
};

export type UseOpenXAgentFeedback = () => {
  submitFeedback: (payload: OpenXAgentFeedbackPayload) => Promise<void>;
  isSubmitting: boolean;
  error: string | null;
  resetError: () => void;
};

const useOpenXAgentRegisterFeedbackMutation = (): [OpenXAgentRegisterFeedbackMutationFn, { loading: boolean }] => {
  // this is a placeholder feedback mutation
  // it should be replaced along with 'useDealTroubleshootingFeedback' return type
  // and 'DealTroubleshootingFeedbackPayload' payload type
  // and all of the above types
  // once appropriate backend functionalities are implemented

  return [
    async () => ({
      data: {},
    }),
    {
      loading: false,
    },
  ];
};

export const useMessageFeedback: UseOpenXAgentFeedback = () => {
  const [error, setError] = useState<string | null>(null);
  const [registerFeedback, { loading: isSubmitting }] = useOpenXAgentRegisterFeedbackMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const submitFeedback = useCallback(
    async (payload: OpenXAgentFeedbackPayload) => {
      setError(null);
      try {
        const { data } = await registerFeedback({
          variables: {
            langfuse_trace_id: payload.langfuseTraceId,
            score: payload.score,
            comment: payload.comment?.trim() || null,
          },
        });

        const response = data?.openXAgentFeedback;

        if (!response) {
          throw new Error('Failed to feedback: empty response.');
        }

        enqueueSnackbar(t('Thank you for submitting feedback!'), { variant: 'success' });
      } catch (err) {
        const normalizedError =
          typeof err === 'object' && err !== null && 'message' in err
            ? String((err as { message?: string }).message)
            : t('Failed to submit feedback.');
        setError(normalizedError);
        enqueueSnackbar(normalizedError, { variant: 'error' });
        throw err;
      }
    },
    [registerFeedback, enqueueSnackbar, t]
  );

  return {
    submitFeedback,
    isSubmitting,
    error,
    resetError: () => setError(null),
  };
};
